<template>
    <div>
        <ListData :title="$t('menu.playlists')" 
            :dataTable="playlistsTable" 
            :data="playlists" 
            :totalPages="totalPages"
            :loading="loading"
            :allowMultipleSelect="true"
            :isRowSelectable="isEditor"
            @fetchData="fetchData"
            @addNewData="createPlaylistPage"
            @deleteData="deleteSelected"
            />
        <TriadModalPlaylist />
        <TagsEditor ref="tagsEditor" />
        <SongListSidePanel ref="songListSidePanel" />
    </div>

</template>

<script setup>
import { ref, computed, h, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import TriadEditIcon from "@/components/icons/TriadEditIcon.vue";
import TriadRemoveIcon from "@/components/icons/TriadRemoveIcon.vue";
import TriadModalPlaylist from '@/components/TriadModalPlaylist.vue';
import TagsViewer from '@/components/common/elements/TagsViewer.vue';
import TagsEditor from '@/components/common/TriadTagsEditor.vue';
import Pagination from '@/models/pagination';
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';
import { useRouter } from 'vue-router';
import playlistModalStore from '@/store/playlist.modal.store';
import playlistService from '@/service/playlist.service';
import { EyeIcon, ComputerDesktopIcon, ListBulletIcon } from '@heroicons/vue/24/outline';
import Tooltip from '@/components/common/elements/TriadTooltip.vue';
import {appEvents} from '@/store/app.events';

const router = useRouter();
const { t } = useI18n();
const songListSidePanel = ref(null);

const isEditor = (element) => element.permissions?.includes('editor');

const loading = ref(true);
const reloadingTable = ref(false);
const filter = ref({
    pagination: new Pagination(1,10)
});

const totalPages = ref(0);

const playlistsTable = computed(() => ({
    columns: [
        { title: t('table.name'), field: 'name', rowStyle: 'w-full md:w-1/4 md:min-w-[180px] font-medium truncate overflow-hidden'},
        { title: t('table.category'), 
            component: (element) => {
                return h(TagsViewer, { 
                    element: element,
                    tags: element.tags || [],
                    visibleAddButton: isEditor(element) 
                })
            },
            rowStyle: 'w-1/4 min-w-[180px] hidden lg:table-cell'
        },
        { title: t('table.owner'), field: 'user.full_name', rowStyle: 'w-1/6 min-w-[140px] hidden lg:table-cell text-gray-500'},
        { title: t('table.songs'), field: 'songs.length', rowStyle: 'w-20 text-center hidden lg:table-cell'},
        { title: t('table.createdAt'), field: 'created_at', rowStyle: 'w-28 hidden lg:table-cell text-gray-500', format: (element) => {
            if (element.created_at) { 
                return new Date(element.created_at).toLocaleDateString(t('menu.language') === 'Portuguese' ? 'pt-BR' : 'en-US', 
                    { year: 'numeric', month: 'short', day: 'numeric' })
            } else {
                return '-'
            }
        }},
        { 
            properties: () => ({
                tooltip: t('playlist.play')
            }),
            component: (element) => {
                const hasSongs = element.songs && element.songs.length > 0;
                return h(Tooltip, { text: t('playlist.play'), position: 'left' }, 
                             h(ComputerDesktopIcon, 
                                { class: hasSongs ? 'w-6 h-6 min-w-[24px] flex-shrink-0 text-gray-900 dark:text-gray-100 cursor-pointer' : 'w-6 h-6 min-w-[24px] flex-shrink-0 text-gray-400 cursor-not-allowed' }))
            },
            action: (element) => {
                if (element.songs && element.songs.length > 0) {
                    router.push({path: `/playlist/${element.id}/song/${element.songs[0].song_id}`})
                }
            },
            rowStyle: 'w-[50px] min-w-[50px] max-w-[50px]'
        },
        {
            properties: () => ({
                tooltip: t('table.songs')
            }),
            component: () => h(ListBulletIcon, { class: 'w-6 h-6 min-w-[24px] flex-shrink-0 text-gray-900 dark:text-gray-100 cursor-pointer' }),
            action: (element) => {
                songListSidePanel.value.openForPlaylist(element);
            },
            rowStyle: 'w-[50px] min-w-[50px] max-w-[50px]'
        },
        { 
            properties: () => ({
                tooltip: t('table.edit')
            }),
            component: (element) => {
                if (isEditor(element)) {
                    return TriadEditIcon
                } else {
                    return h(Tooltip, { text: 'View', position: 'left' }, 
                    h(EyeIcon, { class: 'w-6 h-6 min-w-[24px] flex-shrink-0 text-gray-900 dark:text-gray-100 cursor-pointer' }))
                }
            },
            action: (element) => {
                router.push({path: `/playlist/${element.id}`})
            },
            rowStyle: 'w-[50px] min-w-[50px] max-w-[50px]'
        },
        { 
            properties: () => ({
                tooltip: t('table.delete')
            }),
            component: TriadRemoveIcon,
            action: (element) => {
                removePlaylist(element.id)
            },
            disabled: (element) => !isEditor(element),
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        
    ]
}));
const playlists = ref([])

const fetchData = async (f) => {
    filter.value = f || filter.value;
    let query = Object.entries(filter.value).map( f => {
            // Multi-tag: repeated `tag=` params (backend AND semantics)
            if (f[0] === 'tag' && Array.isArray(f[1])) {
                return f[1].map(t => `tag=${encodeURIComponent(t)}`).join('&');
            }
            if (f[1] !== null && typeof f[1] === 'object') {
                if (typeof f[1].toString === 'function' && f[1].toString !== Object.prototype.toString) {
                    return f[1].toString()
                } else {
                    return Object.entries(f[1]).map(e => `${e[0]}=${e[1]}`).join('&')
                }
            } else {
                return `${f[0]}=${encodeURIComponent(f[1])}`
            }
        }).join('&');
    if (!query.includes('sort')) {
        query = query + '&sort=-created_at'
    }
    const response = await fetch(`/api/playlist/paginated?${query}`)
    if  (response.status === 200) {
        const resData = await response.json()
        if (resData && Array.isArray(resData.data)) {
            playlists.value = resData
        } else if (Array.isArray(resData)) {
            playlists.value = { data: resData, totalPages: 1, total: resData.length }
        } else {
            playlists.value = { data: [], totalPages: 0, total: 0 }
        }
        totalPages.value = playlists.value.totalPages || 0
    } else {
        toastStore.dispatch('addMessage', {message: 'Error fetching playlists', icon: 'error'})
    }
    loading.value = false;
    reloadingTable.value = false;
}

const removePlaylist = async (id) => {
    if (await dialogStore.dispatch('confirm', 'Are you sure you want to delete this playlist?')) {
        reloadingTable.value = true;
        const response = await fetch(`/api/playlist/${id}`, {
            method: 'DELETE'
        })
        if (response.status === 200) {
            toastStore.dispatch('addMessage', {message: 'Playlist deleted', icon: 'success'})
            fetchData()
        } else {
            toastStore.dispatch('addMessage', {message: 'Error deleting playlist', icon: 'error'})
            reloadingTable.value = false;
        }
    }
}

const deleteSelected = async (elements) => {
    const owned = (elements || []).filter(isEditor);
    if (owned.length === 0) {
        toastStore.dispatch('addMessage', { message: t('artistInfo.notOwnerAction') || 'Not allowed', icon: 'warning', ttl: 3000 });
        return;
    }
    if (owned.length < (elements || []).length) {
        toastStore.dispatch('addMessage', { message: t('artistInfo.notOwnerAction') || 'Not allowed', icon: 'warning', ttl: 3000 });
    }
    if (await dialogStore.dispatch('confirm', 'Are you sure you want to delete all these playlists?')) {
        reloadingTable.value = true;
        const response = await fetch(`/api/playlist/delete`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ids: owned.map(e => e.id || e._id)
            })
        })
        if (response.status === 200) {
            const data = await response.json()
            toastStore.dispatch('addMessage', {message: data.message, icon: 'success'})
            fetchData()
        } else {
            toastStore.dispatch('addMessage', {message: 'Error deleting playlists', icon: 'error'})
            reloadingTable.value = false;
        }
    }  
}

const createPlaylistPage = () => {
    router.push({path: '/playlist/new'})
}

playlistModalStore.watch( () => playlistModalStore.state.message, () => {
    if (!playlistModalStore.state.error){
        fetchData()
    }
})
const tagsEditor = ref(null);

const createTag = async (element) => {
    if (!element.tags) element.tags = [];
    const aiContext = { type: 'playlist', name: element.name, description: element.description };
    const newTags = await tagsEditor.value.open(element.tags, aiContext);
    if (newTags) {
        toastStore.dispatch('addMessage', {message: 'Saving tags', icon: 'info'});
        playlistService.updateTags(element.id, newTags).then(() => {
            toastStore.dispatch('addMessage', {message: 'Tags updated successfully', icon: 'success'})
            fetchData();
        }).catch(() => {
            toastStore.dispatch('addMessage', {message: 'Error updating tags', icon: 'error'})
        });
    }
}
onMounted(() => {
    appEvents.on('tagsviewer.createTag', createTag)
})
onUnmounted(() => {
    appEvents.off('tagsviewer.createTag', createTag)
})

</script>
